import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Shield, X, ChevronDown, ChevronUp, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useConsent } from './ConsentContext';

export default function ConsentSettingsModal() {
  const {
    showSettings, setShowSettings, consent, updateConsent,
    savePreferences, acceptAll, categories
  } = useConsent();

  const [expandedCategory, setExpandedCategory] = useState(null);

  if (!showSettings) return null;

  const colorClasses = {
    green: 'bg-green-100 text-green-700',
    blue: 'bg-blue-100 text-blue-700',
    purple: 'bg-purple-100 text-purple-700',
    amber: 'bg-amber-100 text-amber-700',
    rose: 'bg-rose-100 text-rose-700'
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4"
        onClick={() => setShowSettings(false)}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
          data-testid="consent-settings-modal"
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-[#006847] to-[#A4D65E]">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Shield className="h-8 w-8 text-white" />
                <div>
                  <h2 className="text-xl font-bold text-white">Privacy Preferences</h2>
                  <p className="text-white/80 text-sm">Manage how your data is used</p>
                </div>
              </div>
              <button onClick={() => setShowSettings(false)} className="text-white/80 hover:text-white p-2">
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            <p className="text-gray-600 mb-6">
              When you visit our website, we may store or retrieve information on your browser,
              mostly in the form of cookies. Choose which categories you allow below.
            </p>

            <div className="space-y-4">
              {categories.map((category) => {
                const Icon = category.icon;
                const isExpanded = expandedCategory === category.id;

                return (
                  <div key={category.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="flex items-center justify-between p-4 bg-gray-50">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${colorClasses[category.color]}`}>
                          <Icon className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{category.name}</span>
                            {category.required && (
                              <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded">Required</span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{category.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => !category.required && updateConsent(category.id, !consent[category.id])}
                          disabled={category.required}
                          className={`w-12 h-6 rounded-full transition-colors relative ${
                            consent[category.id] ? 'bg-[#006847]' : 'bg-gray-300'
                          } ${category.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                            consent[category.id] ? 'left-7' : 'left-1'
                          }`} />
                        </button>
                        <button onClick={() => setExpandedCategory(isExpanded ? null : category.id)} className="text-gray-400 hover:text-gray-600">
                          {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                        </button>
                      </div>
                    </div>

                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                          <div className="p-4 bg-white border-t border-gray-100">
                            <div className="grid md:grid-cols-2 gap-4">
                              <div>
                                <h4 className="font-medium text-gray-700 text-sm mb-2">What this includes:</h4>
                                <ul className="space-y-1">
                                  {category.examples.map((example) => (
                                    <li key={example} className="text-sm text-gray-600 flex items-center gap-2">
                                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full" />
                                      {example}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              <div>
                                <h4 className="font-medium text-gray-700 text-sm mb-2">Related cookies:</h4>
                                <div className="flex flex-wrap gap-1">
                                  {category.cookies.map((cookie) => (
                                    <span key={cookie} className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded font-mono">
                                      {cookie}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            {/* Info Box */}
            <div className="mt-6 p-4 bg-blue-50 rounded-lg flex gap-3">
              <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Your rights under privacy laws:</p>
                <ul className="mt-1 space-y-1 text-blue-700">
                  <li>- Right to access your personal data</li>
                  <li>- Right to request deletion of your data</li>
                  <li>- Right to withdraw consent at any time</li>
                  <li>- Right to data portability</li>
                </ul>
                <Link to="/privacy-policy" className="text-blue-600 hover:underline mt-2 inline-block">
                  Read full Privacy Policy
                </Link>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-gray-50 flex flex-wrap gap-3 justify-end">
            <Button variant="outline" onClick={() => setShowSettings(false)}>Cancel</Button>
            <Button variant="outline" onClick={savePreferences} className="border-[#006847] text-[#006847]">Save Preferences</Button>
            <Button onClick={acceptAll} className="bg-[#006847] hover:bg-[#005538]">Accept All</Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
